import * as Config from "@/AppConfiguration.json";
import { serialize } from "object-to-formdata";

interface NamedParameters {
	endpoint: string;
	queryParams?: string;
	odataParams?: string;
	signal?: AbortSignal;
}

/**
 * SATOS portal API operations - *INTERNAL* Use SATOSPortal for operations
 */
export class DataAdapter {
	/**
	 * GET data from the SATOS portal API.
	 *
	 * @param endpoint API Endpoint (e.g. "language/en" omit /api/)
	 * @param odataParams OData Parameters (?$)
	 */
	public static async Get<T>(options: NamedParameters): Promise<T> {
		let params = "";

		if (options.queryParams) {
			params = `${options.queryParams}`;
		}

		if (options.odataParams) params += options.odataParams;

		const url = `${this.ResolveApiUrl()}/${options.endpoint}${params ? "/" + params : ""}`;
		try {
			const response = await fetch(url, {
				credentials: "include",
				mode: "cors",
				signal: options.signal,
			});

			const result = await response.json();

			if (result["@odata.context"]) {
				return result.value as T;
			}

			return result as T;
		} catch (error) {
			console.error("GET error", error);
			throw error;
		}
	}

	public static async GetWithErrorThrowing<T>(
		options: NamedParameters,
	): Promise<T> {
		let params = "";

		if (options.queryParams) {
			params = `${options.queryParams}`;
		}

		if (options.odataParams) params += options.odataParams;

		const url = `${this.ResolveApiUrl()}/${options.endpoint}/${params}`;
		try {
			const response = await fetch(url, {
				credentials: "include",
				mode: "cors",
				signal: options.signal,
			});

			if (!response.ok) {
				throw new Error(response.status.toString());
			}

			const result = await response.json();

			if (result["@odata.context"]) {
				return result.value as T;
			}

			return result as T;
		} catch (error) {
			console.error("GET error", error);
			throw error;
		}
	}

	public static async Post<T>(endpoint: string, payload: any): Promise<T> {
		const options: RequestInit = {
			method: "POST",
			mode: "cors",
			credentials: "include", // include, *same-origin, omit
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(payload), // body data type must match "Content-Type" header
		};

		const response = await fetch(
			`${this.ResolveApiUrl()}/${endpoint}`,
			options,
		);
		if (!response.ok) {
			throw new Error(response.statusText);
		}

		if (response.status === 204 || response.status === 201) {
			return {} as T;
		}
		return (await response.json()) as T;
	}

	public static async PostWithNoResponseBody(
		endpoint: string,
		payload: any,
	): Promise<void> {
		const options: RequestInit = {
			method: "POST",
			mode: "cors",
			credentials: "include", // include, *same-origin, omit
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(payload), // body data type must match "Content-Type" header
		};

		const response = await fetch(
			`${this.ResolveApiUrl()}/${endpoint}`,
			options,
		);
		if (!response.ok) {
			throw new Error(response.statusText);
		}

		//return (await response.json()) as T;
	}

	// Overload for sending payload with DELETE request
	public static async Delete<T>(endpoint: string, payload: any): Promise<T>;
	// Overload for DELETE request without payload
	public static async Delete(endpoint: string): Promise<void>;

	public static async Delete<T>(endpoint: string, payload?: any) {
		if (payload !== undefined) {
			const options: RequestInit = {
				method: "DELETE",
				mode: "cors",
				credentials: "include", // include, *same-origin, omit
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(payload), // body data type must match "Content-Type" header
			};
			const response = await fetch(
				`${this.ResolveApiUrl()}/${endpoint}`,
				options,
			);
			if (!response.ok) {
				throw new Error(response.statusText);
			}

			if (response.status === 204) {
				return {} as T;
			}

			return;
		} else {
			const options: RequestInit = {
				method: "DELETE",
				mode: "cors",
				credentials: "include", // include, *same-origin, omit
			};
			const response = await fetch(
				`${this.ResolveApiUrl()}/${endpoint}`,
				options,
			);
			if (!response.ok) {
				throw new Error(response.statusText);
			}
		}
	}

	public static async Put<T>(endpoint: string, payload: any): Promise<T> {
		const options: RequestInit = {
			method: "PUT",
			mode: "cors",
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(payload),
		};

		const response = await fetch(
			`${this.ResolveApiUrl()}/${endpoint}`,
			options,
		);

		if (!response.ok) {
			throw new Error(response.statusText);
		}

		const res = await response.json();

		return res;
	}

	public static async PutWithNoResponseBody(
		endpoint: string,
		payload: any,
	): Promise<void> {
		const options: RequestInit = {
			method: "PUT",
			mode: "cors",
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(payload),
		};

		const response = await fetch(
			`${this.ResolveApiUrl()}/${endpoint}`,
			options,
		);

		if (!response.ok) {
			throw new Error(response.statusText);
		}

		// const res = await response.json();
		//
		// return res;
	}

	public static async PutWithNoPayload(endpoint: string) {
		const options: RequestInit = {
			method: "PUT",
			mode: "cors",
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
			},
		};

		const response = await fetch(
			`${this.ResolveApiUrl()}/${endpoint}`,
			options,
		);

		if (!response.ok) {
			throw new Error(response.statusText);
		}
	}

	public static async Patch<T>(endpoint: string, payload: any): Promise<T> {
		const options: RequestInit = {
			method: "PATCH",
			mode: "cors",
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(payload),
		};

		const response = await fetch(
			`${this.ResolveApiUrl()}/${endpoint}`,
			options,
		);

		if (!response.ok) {
			throw new Error(response.statusText);
		}

		if (response.status === 204) {
			return {} as T;
		}

		const res = await response.json();

		return res;
	}

	public static async PatchWithNoPayload(endpoint: string) {
		const options: RequestInit = {
			method: "PATCH",
			mode: "cors",
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
			},
		};

		const response = await fetch(
			`${this.ResolveApiUrl()}/${endpoint}`,
			options,
		);

		if (!response.ok) {
			throw new Error(response.statusText);
		}

		//const res = await response.json();

		//return response.status;
	}

	public static async UploadForm<T>(
		endpoint: string,
		payload: any,
	): Promise<T> {
		const formData = new FormData();

		for (const key in payload) {
			if (!payload.hasOwnProperty(key) || payload[key] === undefined) {
				continue;
			} else if (Array.isArray(payload[key])) {
				payload[key].forEach((obj, index) =>
					formData.append(`${key}[${index}]`, obj),
				);
				continue;
			}

			formData.append(key, payload[key]);
		}

		return await this.Upload<T>(endpoint, formData);
	}

	public static async UploadFormPatch<T>(
		endpoint: string,
		payload: any,
	): Promise<T> {
		const formData = new FormData();

		for (const key in payload) {
			if (!payload.hasOwnProperty(key) || payload[key] === undefined) {
				continue;
			}

			formData.append(key, payload[key]);
		}

		return await this.UploadPatch<T>(endpoint, formData);
	}

	public static async PutForm<T>(endpoint: string, payload: any): Promise<T> {
		const formData = new FormData();

		for (const key in payload) {
			if (!payload.hasOwnProperty(key) || payload[key] === undefined) {
				continue;
			}

			formData.append(key, payload[key]);
		}

		return await this.UploadPut<T>(endpoint, formData);
	}

	public static async PutFormObject<T>(
		endpoint: string,
		payload: any,
	): Promise<T> {
		const formData = serialize(payload, {
			indices: false,
			booleansAsIntegers: false,
		});

		return await this.UploadPut<T>(endpoint, formData);
	}
	public static async PutUploadForm<T>(
		endpoint: string,
		payload: any,
	): Promise<T> {
		const formData = new FormData();

		for (const key in payload) {
			if (!payload.hasOwnProperty(key) || payload[key] === undefined) {
				continue;
			} else if (Array.isArray(payload[key])) {
				payload[key].forEach((obj, index) =>
					formData.append(`${key}[${index}]`, obj),
				);
				continue;
			}

			formData.append(key, payload[key]);
		}

		return await this.PutUpload<T>(endpoint, formData);
	}

	public static async PutUploadFormMedia<T>(
		endpoint: string,
		payload: FormData,
	): Promise<T> {
		return await this.PutUpload<T>(endpoint, payload);
	}

	public static async AddUploadFormMedia<T>(
		endpoint: string,
		payload: FormData,
	): Promise<T> {
		return await this.AddUpload<T>(endpoint, payload);
	}

	public static async Upload<T>(
		endpoint: string,
		payload: BodyInit,
	): Promise<T> {
		const options: RequestInit = {
			method: "POST",
			mode: "cors",
			credentials: "include",
			body: payload,
		};

		const response = await fetch(
			`${this.ResolveApiUrl()}/${endpoint}`,
			options,
		);
		if (!response.ok) {
			throw new Error(response.statusText);
		}

		return (await response.json()) as T;
	}

	public static async UploadPut<T>(
		endpoint: string,
		payload: BodyInit,
	): Promise<T> {
		const options: RequestInit = {
			method: "PUT",
			mode: "cors",
			credentials: "include",
			body: payload,
		};

		const response = await fetch(
			`${this.ResolveApiUrl()}/${endpoint}`,
			options,
		);

		if (!response.ok) {
			throw new Error(response.statusText);
		}

		// Check if response body is empty
		const responseBody = await response.text();
		if (!responseBody) {
			return {} as T; // Return an empty object if the response body is empty
		}

		return JSON.parse(responseBody) as T;
	}

	public static async UploadPatch<T>(
		endpoint: string,
		payload: BodyInit,
	): Promise<T> {
		const options: RequestInit = {
			method: "PATCH",
			mode: "cors",
			credentials: "include",
			body: payload,
		};

		const response = await fetch(
			`${this.ResolveApiUrl()}/${endpoint}`,
			options,
		);
		if (!response.ok) {
			throw new Error(response.statusText);
		}

		return (await response.json()) as T;
	}

	public static async UploadImage<T>(
		endpoint: string,
		payload: File,
	): Promise<T> {
		const formData = new FormData();

		if (payload) {
			formData.append("Image", payload);
		}

		return await this.PutUpload<T>(endpoint, formData);
	}

	public static async PutUpload<T>(
		endpoint: string,
		payload: FormData,
	): Promise<T> {
		const options: RequestInit = {
			method: "PUT",
			mode: "cors",
			credentials: "include",
			body: payload,
		};

		const response = await fetch(
			`${this.ResolveApiUrl()}/${endpoint}`,
			options,
		);

		if (!response.ok) {
			throw new Error(response.statusText);
		}

		const responseBody = await response.text();
		if (!responseBody) {
			return {} as T; // Return an empty object if the response body is empty
		}

		return JSON.parse(responseBody) as T;
	}

	public static async AddUpload<T>(
		endpoint: string,
		payload: FormData,
	): Promise<T> {
		const options: RequestInit = {
			method: "POST",
			mode: "cors",
			credentials: "include",
			body: payload,
		};

		const response = await fetch(
			`${this.ResolveApiUrl()}/${endpoint}`,
			options,
		);

		if (!response.ok) {
			throw new Error(response.statusText);
		}

		const responseBody = await response.text();
		if (!responseBody) {
			return {} as T; // Return an empty object if the response body is empty
		}

		return JSON.parse(responseBody) as T;
	}
	public static async PostForm<T>(endpoint: string, payload: any): Promise<T> {
		const formData = new FormData();

		for (const key in payload) {
			if (!payload.hasOwnProperty(key) || payload[key] === undefined) {
				continue;
			}

			formData.append(key, payload[key]);
		}

		const options: RequestInit = {
			method: "POST",
			mode: "cors",
			credentials: "include",
			body: formData,
		};

		const response = await fetch(
			`${this.ResolveApiUrl()}/${endpoint}`,
			options,
		);

		if (!response.ok) {
			throw new Error(response.statusText);
		}

		return (await response.json()) as T;
	}

	public static async PostFormObject<T>(
		endpoint: string,
		payload: any,
	): Promise<T> {
		const formData = serialize(payload, {
			indices: false,
			booleansAsIntegers: true,
		});

		const response = await fetch(`${this.ResolveApiUrl()}/${endpoint}`, {
			method: "POST",
			mode: "cors",
			credentials: "include",
			body: formData,
		});

		if (!response.ok) {
			throw new Error(response.statusText);
		}

		return (await response.json()) as T;
	}

	private static ResolveApiUrl() {
		if (process.env.NODE_ENV === "development" || !window) {
			return Config.API_URL;
		}
		// Resolves to {https}://{devtest.satos.no}/api
		return `${window.location.protocol}//${window.location.host}/api`;
	}
}
